import { Controller } from "stimulus";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [
    "page",
    "card",
    "form",
    "correctRadioButton",
    "correctCheckbox",
    "correctInputError"
  ];

  static values = {
    unsavedTitle: String,
    unsavedMessage: String,
    unsavedMessageNewPage: String,
    unsavedCancel: String,
    unsavedAllow: String,
    unsavedAllowNewPage: String
  };

  connect() {
    this._boundPageHandler = this._switchPageFromEvent.bind(this);
    this._boundRadioChanged = this._onRadioChanged.bind(this);
    this._boundCheckboxChanged = this._onCheckboxChanged.bind(this);
    this._boundCorrectAlternativeChanged = this._syncCorrectAnswer.bind(this);

    document.addEventListener('promote:questionnaire-builder:switch-page', this._boundPageHandler);
    document.addEventListener(
      'questionnaires--content-tab:correctAlternativeChanged',
      this._boundCorrectAlternativeChanged
    );

    if (this.hasCorrectRadioButtonTarget) {
      this.correctRadioButtonTargets.forEach(radio => {
        radio.addEventListener('change', this._boundRadioChanged);
      });
    }

    if (this.hasCorrectCheckboxTarget) {
      this.correctCheckboxTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this._boundCheckboxChanged);
      });
    }
  }

  disconnect() {
    document.removeEventListener(
      'promote:questionnaire-builder:switch-page',
      this._boundPageHandler
    );

    document.removeEventListener(
      'questionnaires--content-tab:correctAlternativeChanged',
      this._boundCorrectAlternativeChanged
    );

    if (this.hasCorrectRadioButtonTarget) {
      this.correctRadioButtonTargets.forEach(radio => {
        radio.removeEventListener('change', this._boundRadioChanged);
      });
    }

    if (this.hasCorrectCheckboxTarget) {
      this.correctCheckboxTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this._boundCheckboxChanged);
      });
    }
  }

  correctRadioButtonTargetConnected(target) {
    target.removeEventListener('change', this._boundRadioChanged);
    target.addEventListener('change', this._boundRadioChanged);
  }

  correctRadioButtonTargetDisconnected(target) {
    target.removeEventListener('change', this._boundRadioChanged);
  }

  correctCheckboxTargetConnected(target) {
    target.removeEventListener('change', this._boundCheckboxChanged);
    target.addEventListener('change', this._boundCheckboxChanged);
  }

  correctCheckboxTargetDisconnected(target) {
    target.removeEventListener('change', this._boundCheckboxChanged);
  }

  async addPage(event) {
    event.preventDefault();

    const url = event.target.dataset.addPageUrl;

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');

    if (unloadChecker) {
      try {
        await unloadChecker.verify({
          confirmTitle: this.unsavedTitleValue,
          confirmMessage: this.unsavedMessageNewPageValue,
          confirmCancel: this.unsavedCancelValue,
          confirmAllow: this.unsavedAllowNewPageValue
        });
      } catch {
        return;
      }
    }

    Rails.ajax({
      type: 'POST',
      url,
      dataType: 'script'
    });
  }

  async switchPage(event) {
    if (event.target.closest('button, a, input, [role="button"]')) return;

    const url = event.currentTarget.dataset.url;

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');

    let hasPending = false;
    const formIsInvalid = this.hasFormTarget && !this.formTarget.checkValidity();

    if (unloadChecker) {
      hasPending = await unloadChecker.hasPendingForms();
    }

    if (hasPending || formIsInvalid) {
      try {
        await new Promise((resolve, reject) => {
          Rails.confirm(null, this.element, {
            confirmTitle: this.unsavedTitleValue,
            confirmMessage: this.unsavedMessageValue,
            confirmCancel: this.unsavedCancelValue,
            confirmAllow: this.unsavedAllowValue
          }, { confirmCallback: resolve, cancelCallback: reject });
        });
      } catch {
        return;
      }

      if (this.hasFormTarget) {
        const saved = await this._saveCurrentForm();
        if (!saved) return;
      }
    }

    Rails.ajax({
      type: 'GET',
      url,
      dataType: 'script',
      error: () => {
        this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
      }
    });
  }

  _saveCurrentForm() {
    if (!this._validate()) return Promise.resolve(false);

    if (this.hasFormTarget && !this.formTarget.checkValidity()) {
      const formErrorsController = this.application.controllers.find(
        c => c.identifier === 'form-errors' && c.element === this.formTarget
      );
      formErrorsController?.validateForm();
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const form = this.formTarget;
      let saved = true;

      const handleError = () => { saved = false; };

      document.addEventListener(
        'promote:display-error-message',
        handleError,
        { once: true }
      );

      Rails.ajax({
        url: form.action,
        type: (form.dataset.method || 'patch').toUpperCase(),
        data: new FormData(form),
        dataType: 'script',
        success: () => {
          document.removeEventListener('promote:display-error-message', handleError);
          resolve(saved);
        },
        error: () => {
          document.removeEventListener('promote:display-error-message', handleError);
          resolve(false);
        }
      });
    });
  }

  async deletePage(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const row = button.closest('[data-questionnaires--content-tab-target="page"]');

    // Case 3: already on this page — show confirmation and delete
    if (row.classList.contains('active')) {
      Rails.confirm(null, button, button.dataset, {
        confirmCallback: () => {
          Rails.ajax({
            type: 'DELETE',
            url: button.dataset.deleteUrl,
            dataType: 'script',
            error: () => {
              this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
            }
          });
        }
      });
      return;
    }

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');
    const hasPending = unloadChecker ? await unloadChecker.hasPendingForms() : false;

    if (!hasPending) {
      // Case 2: no unsaved changes — show delete confirmation immediately
      const activeRow = this.pageTargets.find(p => p.classList.contains('active'));
      const activePageId = activeRow
        ? activeRow.id.replace('questionnaires-builder-vat-item-', '')
        : null;

      Rails.confirm(null, button, button.dataset, {
        confirmCallback: () => {
          Rails.ajax({
            type: 'DELETE',
            url: button.dataset.deleteUrl,
            data: activePageId ? `selected_page_id=${activePageId}` : '',
            dataType: 'script',
            error: () => {
              this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
            }
          });
        }
      });
      return;
    }

    // Case 1: unsaved changes — warn first, then switch to target page on confirm
    try {
      await unloadChecker.verify({
        confirmTitle: this.unsavedTitleValue,
        confirmMessage: this.unsavedMessageValue,
        confirmCancel: this.unsavedCancelValue,
        confirmAllow: this.unsavedAllowValue
      });
    } catch {
      return;
    }

    Rails.ajax({
      type: 'GET',
      url: row.dataset.url,
      dataType: 'script',
      error: () => {
        this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
      }
    });
  }

  addQuestionPart(event) {
    event.preventDefault();

    const type = event.currentTarget.dataset.questionPartType;
    const url  = event.currentTarget.dataset.questionPartsUrl;

    Rails.ajax({
      type: 'POST',
      url,
      data: `question_part[type]=${encodeURIComponent(type)}`,
      dataType: 'script'
    });
  }

  addAlternative(event) {
    event.preventDefault();

    const url = event.currentTarget.dataset.alternativesUrl;

    Rails.ajax({
      type: 'POST',
      url,
      dataType: 'script'
    });
  }

  deleteQuestionPart(event) {
    event.preventDefault();

    const button = event.currentTarget;

    Rails.confirm(null, button, button.dataset, {
      confirmCallback: () => {
        Rails.ajax({
          type: 'DELETE',
          url: button.dataset.deleteUrl,
          dataType: 'script',
          error: () => {
            this.element.dispatchEvent(
              new CustomEvent('ajax:error', { bubbles: true })
            );
          }
        });
      }
    });
  }

  deleteQuestionAlternative(event) {
    event.preventDefault();
    const button = event.currentTarget;
    Rails.confirm(null, button, button.dataset, {
      confirmCallback: () => {
        Rails.ajax({
          type: 'DELETE',
          url: button.dataset.deleteUrl,
          dataType: 'script',
          error: () => {
            this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
          }
        });
      }
    });
  }

  // The card header leads with the question title, and dragging collapses every
  // card, so the header is the only thing on screen while reordering. Keeping it
  // in step with the input avoids dragging a card labelled with a stale title.
  syncQuestionTitle(event) {
    const card = event.target.closest('[data-test-question-card]');
    const display = card && card.querySelector('[data-question-title]');

    if (display) display.textContent = event.target.value;
  }

  selectCard(event) {
    event.stopPropagation();

    this.cardTargets.forEach(c => c.classList.remove('active'));

    event.currentTarget.classList.add('active');
  }

  onFormSubmit(event) {
    if (!this._validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  _validate() {
    let isValid = true;

    this.correctInputErrorTargets.forEach(error => {
      const questionCard = error.closest('.js-question-card');
      const inputs = [
        ...this.correctRadioButtonTargets,
        ...this.correctCheckboxTargets
      ].filter(i => questionCard.contains(i));

      if (!inputs.some(i => i.checked)) {
        this._showCorrectAnswerMissingError(questionCard);
        isValid = false;
      } else {
        this._hideCorrectAnswerMissingError(questionCard);
      }
    });
    return isValid;
  }

  _onRadioChanged(event) {
    const questionCard = event.target.closest('.js-question-card');

    const allAnswersFromQuestionExcept = this.correctRadioButtonTargets
      .filter(input => input !== event.target && questionCard.contains(input));

    allAnswersFromQuestionExcept.forEach(input => { input.checked = false; });

    this._hideCorrectAnswerMissingError(questionCard);
  }

  _onCheckboxChanged(event) {
    const questionCard = event.target.closest('.js-question-card');

    const anyChecked = this.correctCheckboxTargets
      .filter(input => questionCard.contains(input))
      .some(input => input.checked);

    if (anyChecked) {
      this._hideCorrectAnswerMissingError(questionCard);
    } else {
      this._showCorrectAnswerMissingError(questionCard);
    }
  }

  _showCorrectAnswerMissingError(questionCard) {
    this.correctInputErrorTargets
      .filter(e => questionCard.contains(e))
      .forEach(e => e.classList.remove('hidden'));
  }

  _hideCorrectAnswerMissingError(questionCard) {
    this.correctInputErrorTargets
      .filter(e => questionCard.contains(e))
      .forEach(e => e.classList.add('hidden'));
  }

  _syncCorrectAnswer({ detail: { questionPartId, correctAlternativeIds } }) {
    const container = document
      .querySelector(`[data-alternative-container="${questionPartId}"]`);

    if (!container) {
      return;
    }

    container.querySelectorAll('[data-alternative-card]').forEach(card => {
      const altId = parseInt(card.dataset.alternativeCard, 10);
      const isCorrect = correctAlternativeIds.includes(altId);

      const input = card.querySelector("input[name$='[correct]'][value='true']");
      if (input) {
        input.checked = isCorrect;
      }

      const guard = card.querySelector('[data-delete-correct-guard]');
      const link  = card.querySelector('[data-delete-link]');

      if (guard) guard.classList.toggle('hidden', !isCorrect);
      if (link)  link.classList.toggle('hidden', isCorrect);
    });
  }

  _switchPageFromEvent(event) {
    const pageId = event.detail.id;

    this.pageTargets.forEach(page => {
        if (page.id.split('-').at(-1) === pageId) {
          page.classList.add('active')
        } else {
          page.classList.remove('active')
        }
      }
    );
  }
}
